import type {
  CreateDataModelRequest,
  CreateDataModelResponse,
  DeleteDataModelResponse,
  GetDataModelBuilderResponse,
  SaveDataModelRequest,
  UpdateDataModelRequest,
  UpdateDataModelResponse,
} from "./dataModelBuilder.types";
import { MOCK_LATENCY_MS } from "./dataModels.constants";
import type { DataModel } from "./dataModels.types";
import type { DataModelRelation } from "./dataModelBuilder.types";
import {
  normalizeDataModelSourceHandle,
  normalizeDataModelTargetHandle,
} from "./dataModelRelationEdges";
import {
  createMockId,
  countTablesAndMetrics,
  getMockBuilder,
  removeMockListItem,
  setMockBuilder,
  upsertMockListItem,
  type BuilderDocument,
} from "./dataModels.mockStore";

const normalizeMockRelation = (relation: DataModelRelation): DataModelRelation => ({
  ...relation,
  sourceHandle: relation.sourceHandle
    ? normalizeDataModelSourceHandle(relation.sourceHandle)
    : undefined,
  targetHandle: relation.targetHandle
    ? normalizeDataModelTargetHandle(relation.targetHandle)
    : undefined,
});

const toBuilderResponse = (doc: BuilderDocument): GetDataModelBuilderResponse => ({
  data: {
    id: doc.id,
    name: doc.name,
    description: doc.description,
    status: doc.status,
    version: doc.version,
    tables: doc.tables,
    metricIds: doc.metricIds,
    relations: doc.relations.map(normalizeMockRelation),
    tableSummaries: doc.tableSummaries,
    metricSummaries: doc.metricSummaries,
  },
});

const toListItem = (doc: BuilderDocument): DataModel => {
  const { tablesCount, metricsCount } = countTablesAndMetrics(
    doc.tables,
    doc.metricIds,
  );
  return {
    id: doc.id,
    name: doc.name,
    description: doc.description,
    status: doc.status,
    tablesCount,
    metricsCount,
    isDefault: doc.isDefault,
    createdAt: doc.createdAt,
    updatedAt: doc.updatedAt,
  };
};

const requestToDocument = (
  id: string,
  body: SaveDataModelRequest,
  existing?: BuilderDocument,
): BuilderDocument => {
  const timestamp = new Date().toISOString();
  const { tablesCount, metricsCount } = countTablesAndMetrics(
    body.tables,
    body.metricIds,
  );
  void tablesCount;
  void metricsCount;

  return {
    id,
    name: body.name,
    description: body.description,
    status: body.status,
    version: existing ? existing.version + 1 : 1,
    tables: body.tables,
    metricIds: body.metricIds,
    relations: body.relations,
    isDefault: existing?.isDefault ?? false,
    createdAt: existing?.createdAt ?? timestamp,
    updatedAt: timestamp,
  };
};

export const mockFetchDataModelBuilder = async (
  dataModelId: string,
): Promise<GetDataModelBuilderResponse> => {
  await new Promise((resolve) => setTimeout(resolve, MOCK_LATENCY_MS));
  const doc = getMockBuilder(dataModelId);
  if (!doc) {
    throw new Error(`Data model not found: ${dataModelId}`);
  }
  return toBuilderResponse(doc);
};

export const mockCreateDataModel = async (
  body: CreateDataModelRequest,
): Promise<CreateDataModelResponse> => {
  await new Promise((resolve) => setTimeout(resolve, MOCK_LATENCY_MS));

  const id = createMockId();
  const doc = requestToDocument(id, body);
  setMockBuilder(doc);
  upsertMockListItem(toListItem(doc));

  return {
    data: {
      id: doc.id,
      name: doc.name,
      description: doc.description,
      status: doc.status,
      version: doc.version,
      tablesCount: doc.tables.length,
      metricsCount: doc.metricIds.length,
      createdAt: doc.createdAt,
      updatedAt: doc.updatedAt,
    },
  };
};

export const mockUpdateDataModel = async (
  dataModelId: string,
  body: UpdateDataModelRequest,
): Promise<UpdateDataModelResponse> => {
  await new Promise((resolve) => setTimeout(resolve, MOCK_LATENCY_MS));

  const existing = getMockBuilder(dataModelId);
  if (!existing) {
    throw new Error(`Data model not found: ${dataModelId}`);
  }
  if (body.version !== existing.version) {
    const err = new Error("Version conflict");
    (err as Error & { status: number }).status = 409;
    throw err;
  }

  const doc = requestToDocument(dataModelId, body, existing);
  setMockBuilder(doc);
  upsertMockListItem(toListItem(doc));

  return {
    data: {
      id: doc.id,
      name: doc.name,
      description: doc.description,
      status: doc.status,
      version: doc.version,
      tablesCount: doc.tables.length,
      metricsCount: doc.metricIds.length,
      createdAt: doc.createdAt,
      updatedAt: doc.updatedAt,
    },
  };
};

export const mockDeleteDataModel = async (
  dataModelId: string,
): Promise<DeleteDataModelResponse> => {
  await new Promise((resolve) => setTimeout(resolve, MOCK_LATENCY_MS));
  const removed = removeMockListItem(dataModelId);
  if (!removed) {
    throw new Error(`Cannot delete data model: ${dataModelId}`);
  }
  return { success: true, deletedId: dataModelId };
};
